// NotifyRepModal.tsx
// Modal for sending a signature-request email to a representative.
// Pre-populates To, Subject, and Body; allows editing before sending.

import React, { useState } from 'react';
import { X, Send, Mail } from 'lucide-react';
import { notifyRep } from '../../services/poa';

interface Props {
  poaId:        string;
  repOrder:     number;
  repName:      string;
  repEmail:     string;        // pe_staff_user
  taxpayerName: string;
  taxYear:      number | null;
  from:         string;        // creator's email (sender)
  onClose:      () => void;
  onSent:       () => void;
}

function buildSignUrl(poaId: string, repOrder: number) {
  return `${window.location.origin}/poa/${poaId}?sign=${repOrder}`;
}

function buildDefaultSubject(taxpayerName: string) {
  return `Action Required: Please Sign Form 2848 for ${taxpayerName}`;
}

// Plain text shown in the editable textarea
function buildDefaultBody(taxpayerName: string, taxYear: number | null, from: string) {
  const yearLine = taxYear ? ` for the ${taxYear} tax year` : '';
  return `Hello,

Your signature is required on a Form 2848 (Power of Attorney)${yearLine} for ${taxpayerName}.

Please click the button in this email to open the document — a signature window will appear for you automatically.

This request was sent by ${from} via the BMSS Admin Portal. If you have any questions, please reply to this email.`;
}

// HTML version sent to Outlook — converts the plain text message + injects a styled CTA button
function buildHtmlBody(plainText: string, signUrl: string) {
  const logoUrl = 'https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/KB-Favicon-New.png';

  const escaped = plainText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .split('\n')
    .map((line) => line.trim() === '' ? '<br>' : `<p style="margin:0 0 12px 0;color:#374151;font-size:14px;line-height:1.6;">${line}</p>`)
    .join('\n');

  return `<!DOCTYPE html>
<html>
<body style="margin:0;padding:0;background:#f3f4f6;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f3f4f6;padding:32px 16px;">
    <tr><td align="center">
      <table width="580" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,.08);">

        <!-- Header -->
        <tr>
          <td style="background:#1e293b;padding:20px 32px;">
            <table cellpadding="0" cellspacing="0" width="100%">
              <tr>
                <td style="vertical-align:middle;">
                  <!-- Logo + wordmark -->
                  <table cellpadding="0" cellspacing="0">
                    <tr>
                      <td style="vertical-align:middle;padding-right:10px;">
                        <img src="${logoUrl}" alt="Botfield" width="36" height="36"
                             style="display:block;border-radius:6px;" />
                      </td>
                      <td style="vertical-align:middle;">
                        <span style="font-size:17px;font-weight:700;color:#ffffff;letter-spacing:.3px;">Admin HQ</span>
                      </td>
                    </tr>
                  </table>
                </td>
                <td align="right" style="vertical-align:middle;">
                  <p style="margin:0;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:1px;">BMSS &amp; Botfield</p>
                </td>
              </tr>
            </table>
            <!-- Divider -->
            <table cellpadding="0" cellspacing="0" width="100%">
              <tr><td style="padding-top:16px;border-top:1px solid #334155;font-size:16px;font-weight:700;color:#e2e8f0;">
                Form 2848 — Signature Request
              </td></tr>
            </table>
          </td>
        </tr>

        <!-- Body -->
        <tr>
          <td style="padding:32px 32px 8px 32px;">
            ${escaped}
          </td>
        </tr>

        <!-- CTA Button -->
        <tr>
          <td align="center" style="padding:16px 32px 32px 32px;">
            <table cellpadding="0" cellspacing="0">
              <tr>
                <td style="background:#2563eb;border-radius:6px;">
                  <a href="${signUrl}" target="_blank"
                     style="display:inline-block;padding:13px 28px;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;letter-spacing:.2px;">
                    ✍&nbsp; Open &amp; Sign Form 2848
                  </a>
                </td>
              </tr>
            </table>
            <p style="margin:12px 0 0 0;font-size:11px;color:#9ca3af;">
              Or copy this link into your browser:<br>
              <a href="${signUrl}" style="color:#6b7280;word-break:break-all;">${signUrl}</a>
            </p>
          </td>
        </tr>

        <!-- Footer -->
        <tr>
          <td style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:16px 32px;">
            <p style="margin:0;font-size:11px;color:#9ca3af;">
              This email was sent via the BMSS Admin Portal. Please do not reply to this message directly — reply to the sender listed above.
            </p>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4, fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'rgba(255,255,255,.04)', color: 'var(--text)',
  fontSize: 13, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const readonlyStyle: React.CSSProperties = {
  ...inputStyle,
  opacity: 0.55, cursor: 'default',
};

export default function NotifyRepModal({
  poaId, repOrder, repName, repEmail, taxpayerName, taxYear, from, onClose, onSent,
}: Props) {
  const signUrl = buildSignUrl(poaId, repOrder);

  const [to,      setTo]      = useState(repEmail);
  const [subject, setSubject] = useState(buildDefaultSubject(taxpayerName));
  const [body,    setBody]    = useState(buildDefaultBody(taxpayerName, taxYear, from));
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  async function handleSend() {
    if (!to.trim()) { setError('Recipient email is required.'); return; }
    setError(null);
    setSending(true);
    try {
      await notifyRep({ poaId, repOrder, to: to.trim(), subject, body: buildHtmlBody(body, signUrl), bcc: from });
      onSent();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(3px)',
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--surface, #1e2030)',
        border: '1px solid var(--border)',
        borderRadius: 16, padding: 0,
        width: 580, maxWidth: '95vw', maxHeight: '90vh',
        boxShadow: '0 20px 60px rgba(0,0,0,.7)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Mail size={17} color="var(--accent)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Notify Representative</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
                Request signature from {repName}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>

          {/* From (read-only) */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>From (your mailbox)</label>
            <input style={readonlyStyle} value={from} readOnly />
          </div>

          {/* To */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>To *</label>
            <input
              style={inputStyle}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@bmss.com"
            />
          </div>

          {/* Subject */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Subject</label>
            <input
              style={inputStyle}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Body */}
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Message</label>
            <textarea
              style={{ ...inputStyle, height: 220, resize: 'vertical', lineHeight: 1.6 }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          {/* Link preview */}
          <div style={{
            padding: '8px 12px', borderRadius: 8, fontSize: 11,
            background: 'rgba(122,162,255,.07)', border: '1px solid rgba(122,162,255,.2)',
            color: 'var(--muted)', wordBreak: 'break-all',
          }}>
            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>Sign link:</span> {signUrl}
          </div>

          {error && (
            <div style={{ marginTop: 12, color: 'var(--bad)', fontSize: 13 }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button className="btn" onClick={onClose} style={{ fontSize: 13 }}>
            Cancel
          </button>
          <button
            className="btn"
            disabled={sending}
            onClick={handleSend}
            style={{
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.4)',
              color: 'var(--accent)', opacity: sending ? 0.5 : 1,
            }}
          >
            <Send size={13} /> {sending ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
